import { useTheme } from '@/context/ThemeContext';

export default function Services() {
  const { isDark } = useTheme();

  const services = [
    { icon: 'ri-loop-right-line', title: 'AI Workflow Automation', description: 'Automate internal operations, scheduling, routing, follow-ups, and administrative tasks.', large: true },
    { icon: 'ri-user-add-line', title: 'Lead Capture & Conversion Systems', description: 'AI systems that capture, qualify, and nurture leads automatically.', large: true },
    { icon: 'ri-dashboard-line', title: 'Operational Dashboards', description: 'Unified dashboards that centralize business intelligence and operational visibility.', large: false },
    { icon: 'ri-robot-line', title: 'AI Assistants', description: 'Deploy intelligent assistants that handle inbound requests, scheduling, and operational coordination.', large: false },
    { icon: 'ri-flow-chart', title: 'Process Engineering', description: 'Design scalable workflows that eliminate bottlenecks and operational friction.', large: false },
    { icon: 'ri-database-2-line', title: 'CRM Automation Architecture', description: 'Connect marketing, operations, and customer experience into one intelligent system.', large: false },
  ];

  const cardClass = (large: boolean) =>
    `group relative backdrop-blur-xl rounded-3xl border transition-all duration-500 hover:-translate-y-2 cursor-pointer overflow-hidden ${
      large ? 'p-12' : 'p-8'
    } ${
      isDark
        ? 'bg-gradient-to-br from-[#1A2332]/60 to-[#0D1B2A]/60 border-white/5 hover:border-[#14B8A6]/40 hover:shadow-2xl hover:shadow-[#14B8A6]/20'
        : 'bg-white border-gray-100 hover:border-[#14B8A6]/40 hover:shadow-2xl hover:shadow-[#14B8A6]/10'
    }`;

  return (
    <section
      id="services"
      className={`py-32 px-6 relative transition-colors duration-500 ${isDark ? 'bg-[#111D2C]' : 'bg-[#F5F8FA]'}`}
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#14B8A6]/30 to-transparent" />

      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-20">
          <div className="text-[#14B8A6] text-xs font-semibold tracking-[0.2em] uppercase mb-6">CAPABILITIES</div>
          <h2 className={`text-5xl md:text-6xl font-bold mb-4 tracking-tight leading-[1.1] transition-colors duration-500 ${isDark ? 'text-white' : 'text-[#0D1B2A]'}`}>
            What Vilaris AI Builds
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {services.filter((s) => s.large).map((service, index) => (
            <div key={index} className={cardClass(true)}>
              <div className="absolute inset-0 bg-gradient-to-br from-[#14B8A6]/0 to-[#14B8A6]/0 group-hover:from-[#14B8A6]/10 group-hover:to-transparent transition-all duration-500 rounded-3xl" />
              <div className="relative z-10">
                <div className="w-20 h-20 flex items-center justify-center bg-[#14B8A6]/10 rounded-2xl mb-8 group-hover:bg-[#14B8A6]/20 transition-all duration-500 group-hover:scale-110">
                  <i className={`${service.icon} text-5xl text-[#14B8A6]`} />
                </div>
                <h3 className={`text-3xl font-bold mb-4 tracking-tight transition-colors duration-500 ${isDark ? 'text-white' : 'text-[#0D1B2A]'}`}>
                  {service.title}
                </h3>
                <p className={`text-lg leading-relaxed transition-colors duration-500 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {service.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.filter((s) => !s.large).map((service, index) => (
            <div key={index} className={cardClass(false)}>
              <div className="absolute inset-0 bg-gradient-to-br from-[#14B8A6]/0 to-[#14B8A6]/0 group-hover:from-[#14B8A6]/10 group-hover:to-transparent transition-all duration-500 rounded-3xl" />
              <div className="relative z-10">
                <div className="w-14 h-14 flex items-center justify-center bg-[#14B8A6]/10 rounded-xl mb-6 group-hover:bg-[#14B8A6]/20 transition-all duration-500 group-hover:scale-110">
                  <i className={`${service.icon} text-3xl text-[#14B8A6]`} />
                </div>
                <h3 className={`text-xl font-bold mb-3 tracking-tight transition-colors duration-500 ${isDark ? 'text-white' : 'text-[#0D1B2A]'}`}>
                  {service.title}
                </h3>
                <p className={`text-sm leading-relaxed transition-colors duration-500 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {service.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
